import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import {
  InsuranceAttachmentDTO,
  InsuranceDTO,
  MultiMediaResponse,
  PayerEntryDTO,
  InsuranceSearchType,
  InsuranceSettingsDTO,
  InsuranceSlot,
  InsuranceSlotOptionDTO,
  SyncInsuranceAttachmentsRequestDTO,
  UpsertInsuranceRequestDTO,
} from '@/dtos';
import {
  isEmptyString,
} from 'utils/misc';
import {
  getApiPatientGetInsuranceAttachmentsInsuranceId,
  getApiPatientGetUnassociatedInsuranceCardsPatientId,
  getApiPatientPortalInsuranceGetFavorites,
  getApiPatientPortalInsuranceSearch,
  getApiSettingsGetInsuranceSettings,
  postApiPatientSyncInsuranceAttachments,
  postApiPatientPortalChartsAddInsurance,
} from '@/endpoints';
import {
  useCallback,
  useMemo,
} from 'react';
import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import usePrefetchedData from '@/components/hooks/usePrefetchedData';

export const INSURANCE_SLOT_ORDER: Record<InsuranceSlot, number> = {
  [InsuranceSlot.Primary]: 0,
  [InsuranceSlot.PrimaryDME]: 1,
  [InsuranceSlot.Secondary]: 2,
  [InsuranceSlot.Tertiary]: 3,
  [InsuranceSlot.Other]: 4,
};

export function useInsuranceSearch(
  search: string = '',
  searchType: InsuranceSearchType = 'All',
  enabled: boolean = true,
): UseQueryResult<PayerEntryDTO[], Error> {
  return useQuery<PayerEntryDTO[], Error>({
    queryKey: ['insurance-lookup', search],
    queryFn: async () => {
      const insuranceList = await getApiPatientPortalInsuranceSearch({ search, searchType });
      return compact(insuranceList);
    },
    enabled: enabled && !isEmptyString(search),
  });
}

export function useGetFavoriteInsurances(): UseQueryResult<
  PayerEntryDTO[], Error> {
  return useQuery<PayerEntryDTO[], Error>({
    queryKey: ['insurance-favorites'],
    queryFn: async () => {
      const insuranceList = await getApiPatientPortalInsuranceGetFavorites();
      return compact(insuranceList);
    },
  });
}

export function useInsuranceMetaData(enabled: boolean = true) {
  const { data } = usePrefetchedData<InsuranceSettingsDTO>({
    key: ['insurance-meta'],
    fetchFn: getApiSettingsGetInsuranceSettings,
    enabled,
  });

  const { mspTypes = [], relationshipTypes, insuranceSlotOptions = [] } = data ?? {};

  const relationshipTypesOptions = useMemo(
    () => compact(map(relationshipTypes, 'name')),
    [relationshipTypes],
  );

  const relationshipTypesByName = useMemo(
    () => keyBy(relationshipTypes ?? [], 'name'),
    [relationshipTypes],
  );

  const insuranceSlotValues = useMemo(
    () => compact(map(insuranceSlotOptions, 'slot')) as InsuranceSlot[],
    [insuranceSlotOptions],
  );

  const insuranceSlotOptionsBySlot = useMemo(
    () => keyBy(insuranceSlotOptions ?? [], 'slot') as Partial<Record<InsuranceSlot, InsuranceSlotOptionDTO>>,
    [insuranceSlotOptions],
  );

  const getInsuranceSlotLabel = useCallback(
    (slot: InsuranceSlot) => insuranceSlotOptionsBySlot[slot]?.displayName ?? slot,
    [insuranceSlotOptionsBySlot],
  );

  return {
    relationshipTypes: relationshipTypesOptions,
    relationshipTypesByName,
    mspTypes,
    insuranceSlotOptions: insuranceSlotOptions ?? [],
    insuranceSlotValues,
    getInsuranceSlotLabel,
  };
}

export function useAddInsurances(): UseMutationResult<
  InsuranceDTO, Error, UpsertInsuranceRequestDTO> {
  return useMutation<InsuranceDTO, Error, UpsertInsuranceRequestDTO>({
    mutationFn: (data) => postApiPatientPortalChartsAddInsurance(data),
  });
}

export function useGetInsuranceAttachments(
  insuranceId: number = 0,
): UseQueryResult<InsuranceAttachmentDTO[], Error> {
  return useQuery<InsuranceAttachmentDTO[], Error>({
    queryKey: ['insurance-attachments', insuranceId],
    queryFn: () => getApiPatientGetInsuranceAttachmentsInsuranceId(insuranceId),
    enabled: insuranceId > 0,
  });
}

export function useGetUnassociatedInsuranceCards(
  patientId: number = 0,
): UseQueryResult<MultiMediaResponse[], Error> {
  return useQuery<MultiMediaResponse[], Error>({
    queryKey: ['unassociated-insurance-cards', patientId],
    queryFn: async () => {
      const result = await getApiPatientGetUnassociatedInsuranceCardsPatientId(patientId);
      return result?.list ?? [];
    },
    enabled: patientId > 0,
  });
}

export function useSyncInsuranceAttachments(): UseMutationResult<
  InsuranceAttachmentDTO[], Error, SyncInsuranceAttachmentsRequestDTO> {
  return useMutation<InsuranceAttachmentDTO[], Error, SyncInsuranceAttachmentsRequestDTO>({
    mutationFn: (data) => postApiPatientSyncInsuranceAttachments(data),
  });
}

type RestrictedInsuranceSlotConflictParams = {
  currentInsuranceId?: number;
  selectedInsuranceSlot?: InsuranceSlot | null;
  isActiveInsurance: boolean;
};

export function useRestrictedInsuranceSlotConflict(
  insurances: InsuranceDTO[] = [],
  restrictedSlotSet: Set<InsuranceSlot | undefined> = new Set(),
) {
  return useCallback(({
    currentInsuranceId,
    selectedInsuranceSlot,
    isActiveInsurance,
  }: RestrictedInsuranceSlotConflictParams) => Boolean(
    isActiveInsurance
    && !isNil(selectedInsuranceSlot)
    && restrictedSlotSet.has(selectedInsuranceSlot as InsuranceSlot)
    && insurances.some((insuranceItem) => (
      (insuranceItem?.insuranceId ?? 0) !== (currentInsuranceId ?? 0)
      && !insuranceItem?.inactive
      && insuranceItem?.insuranceSlot === selectedInsuranceSlot
    )),
  ), [insurances, restrictedSlotSet]);
}
